import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import OneSignal from 'react-onesignal';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WS_URL = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');
const ONESIGNAL_APP_ID = process.env.REACT_APP_ONESIGNAL_APP_ID;

// Notification sound (short beep)
const NOTIFICATION_SOUND = 'data:audio/wav;base64,UklGRl9vT19XQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YU' + 
  'tvT19' + 'A'.repeat(100); // Placeholder for actual sound

const NotificationContext = createContext(null);

// Helper function to play notification sound
const playNotificationSound = () => {
  try {
    // Create a simple beep using Web Audio API
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.value = 800; // Frequency in Hz
    oscillator.type = 'sine';
    
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.2);
  } catch (error) {
    console.log('Sound not available:', error);
  }
};

// Helper function to vibrate device
const vibrateDevice = (pattern = [100, 50, 100]) => {
  try {
    if ('vibrate' in navigator) {
      navigator.vibrate(pattern);
    }
  } catch (error) {
    console.log('Vibration not available:', error);
  }
};

export const NotificationProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [initialized, setInitialized] = useState(false);
  const [permissionGranted, setPermissionGranted] = useState(false);
  const [preferences, setPreferences] = useState({
    messages: true,
    taps: true,
    matches: true,
    profile_views: true,
    events: true,
    sound: true,
    vibration: true
  });
  const [notifications, setNotifications] = useState([]);
  const lastTapCountRef = useRef(0);
  const pollingRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // WebSocket connection for real-time notifications
  useEffect(() => {
    if (!token || !user?.id) return;

    const connectWebSocket = () => {
      // Only try WebSocket if URL is available
      if (!WS_URL) return;
      
      try {
        const ws = new WebSocket(`${WS_URL}/api/ws/notifications?token=${token}`);
        
        ws.onopen = () => {
          console.log('Notification WebSocket connected');
          // Clear any pending reconnect
          if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
          }
        };
        
        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            handleRealtimeNotification(data);
          } catch (e) {
            console.error('Failed to parse WebSocket message:', e);
          }
        };
        
        ws.onclose = () => {
          console.log('WebSocket disconnected, will fallback to polling');
          wsRef.current = null;
        };
        
        ws.onerror = (error) => {
          console.log('WebSocket error, using polling fallback');
          ws.close();
        };
        
        wsRef.current = ws;
      } catch (error) {
        console.log('WebSocket not available, using polling');
      }
    };

    // Try WebSocket connection
    connectWebSocket();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [token, user?.id]);

  // Handle real-time notification from WebSocket
  const handleRealtimeNotification = useCallback((data) => {
    const { type, payload } = data;
    
    // Play sound and vibrate based on preferences
    if (preferences.sound) {
      playNotificationSound();
    }
    if (preferences.vibration) {
      vibrateDevice(type === 'match' ? [100, 50, 100, 50, 100] : [100, 50, 100]);
    }
    
    if (type === 'tap' && preferences.taps) {
      const tapperName = payload.tapper_name || 'Someone';
      toast(`⚡ ${tapperName} tapped you!`, {
        duration: 4000,
        action: {
          label: 'View',
          onClick: () => window.location.href = `/profile/${payload.tapper_id}`
        }
      });
      
      addNotification({
        type: 'tap',
        user: { id: payload.tapper_id, display_name: tapperName, ...payload },
        message: `${tapperName} tapped you`
      });
    } else if (type === 'match' && preferences.matches) {
      const matchName = payload.match_name || 'Someone';
      toast.success(`🎉 It's a match! You and ${matchName} tapped each other!`, {
        duration: 5000,
        action: {
          label: 'View',
          onClick: () => window.location.href = `/profile/${payload.match_id}`
        }
      });
      
      addNotification({
        type: 'match',
        user: { id: payload.match_id, display_name: matchName, ...payload },
        message: `You matched with ${matchName}!`
      });
    } else if (type === 'message' && preferences.messages) {
      const senderName = payload.sender_name || 'Someone';
      toast(`💬 New message from ${senderName}`, {
        duration: 4000,
        action: {
          label: 'Read',
          onClick: () => window.location.href = `/chat/${payload.sender_id}`
        }
      });
    } else if (type === 'profile_view' && preferences.profile_views) {
      const viewerName = payload.viewer_name || 'Someone';
      toast(`👀 ${viewerName} viewed your profile`, {
        duration: 3000,
        action: {
          label: 'View',
          onClick: () => window.location.href = `/profile/${payload.viewer_id}`
        }
      });
    }
  }, [preferences]);

  // Add notification to list
  const addNotification = useCallback((notif) => {
    setNotifications(prev => [{
      id: Date.now(),
      ...notif,
      timestamp: new Date().toISOString(),
      read: false
    }, ...prev].slice(0, 50));
  }, []);

  // Initialize OneSignal
  useEffect(() => {
    const initOneSignal = async () => {
      // Skip if no app ID or already initialized
      if (!ONESIGNAL_APP_ID || ONESIGNAL_APP_ID.includes('placeholder') || initialized) {
        return;
      }

      try {
        await OneSignal.init({
          appId: ONESIGNAL_APP_ID,
          allowLocalhostAsSecureOrigin: true,
          notifyButton: {
            enable: false, // We'll use our own UI
          },
          promptOptions: {
            slidedown: {
              prompts: [
                {
                  type: 'push',
                  autoPrompt: false, // We'll trigger manually
                  text: {
                    actionMessage: 'Get notified when you receive messages, taps, and matches',
                    acceptButton: 'Allow',
                    cancelButton: 'Not now',
                  },
                },
              ],
            },
          },
        });

        setInitialized(true);

        // Check current permission status
        const permission = await OneSignal.Notifications.permission;
        setPermissionGranted(permission);

        // Listen for permission changes
        OneSignal.Notifications.addEventListener('permissionChange', (perm) => {
          setPermissionGranted(perm);
          if (perm) {
            toast.success('Push notifications enabled!');
          }
        });

        // Handle notification clicks with deep linking
        OneSignal.Notifications.addEventListener('click', (event) => {
          const { url, additionalData } = event.notification;
          
          // Handle deep linking based on notification data
          if (additionalData) {
            const { type, tapper_id, sender_id, viewer_id, event_id, conversation_id } = additionalData;
            
            switch (type) {
              case 'tap':
              case 'match':
                if (tapper_id) {
                  window.location.href = `/profile/${tapper_id}`;
                  return;
                }
                break;
              case 'message':
                if (sender_id) {
                  window.location.href = `/chat/${sender_id}`;
                  return;
                }
                if (conversation_id) {
                  window.location.href = `/messages`;
                  return;
                }
                break;
              case 'profile_view':
                if (viewer_id) {
                  window.location.href = `/profile/${viewer_id}`;
                  return;
                }
                break;
              case 'event_reminder':
                if (event_id) {
                  window.location.href = `/events/${event_id}`;
                  return;
                }
                break;
              default:
                break;
            }
          }
          
          // Fallback to URL if provided
          if (url) {
            // Handle relative URLs
            if (url.startsWith('/')) {
              window.location.href = url;
            } else {
              window.location.href = url;
            }
          } else {
            // Default to activity page
            window.location.href = '/activity';
          }
        });

        // Handle foreground notifications (show toast)
        OneSignal.Notifications.addEventListener('foregroundWillDisplay', (event) => {
          const { heading, content, additionalData } = event.notification;
          
          // Prevent default display (we'll show our own toast)
          event.preventDefault();
          
          // Play sound and vibrate
          if (preferences.sound) {
            playNotificationSound();
          }
          if (preferences.vibration) {
            vibrateDevice();
          }
          
          // Show toast with action
          const notifType = additionalData?.type || 'notification';
          const targetId = additionalData?.tapper_id || additionalData?.sender_id || additionalData?.viewer_id;
          
          toast(content || heading, {
            duration: 4000,
            action: targetId ? {
              label: 'View',
              onClick: () => {
                if (notifType === 'message') {
                  window.location.href = `/chat/${targetId}`;
                } else {
                  window.location.href = `/profile/${targetId}`;
                }
              }
            } : undefined
          });
        });

      } catch (error) {
        console.error('OneSignal init error:', error);
      }
    };

    initOneSignal();
  }, [initialized]);

  // Login user to OneSignal when authenticated
  useEffect(() => {
    const loginToOneSignal = async () => {
      if (!initialized || !user?.id) return;

      try {
        await OneSignal.login(user.id);
      } catch (error) {
        console.error('OneSignal login error:', error);
      }
    };

    loginToOneSignal();
  }, [initialized, user?.id]);

  // Fetch preferences from backend
  useEffect(() => {
    const fetchPreferences = async () => {
      if (!token) return;
      
      try {
        const response = await axios.get(`${API}/users/notification-preferences`);
        setPreferences(response.data.preferences);
      } catch (error) {
        console.error('Failed to fetch notification preferences:', error);
      }
    };

    fetchPreferences();
  }, [token]);

  // Poll for new taps and show in-app toast notifications (fallback when WebSocket not available)
  useEffect(() => {
    if (!token || !user?.id) return;
    
    // Skip polling if WebSocket is connected
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    const checkForNewTaps = async () => {
      // Skip if WebSocket is connected
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        return;
      }
      
      try {
        const response = await axios.get(`${API}/users/taps/received`);
        const currentTaps = response.data || [];
        const currentCount = currentTaps.length;
        
        // If there are more taps than before, show a toast
        if (currentCount > lastTapCountRef.current && lastTapCountRef.current > 0) {
          const newestTap = currentTaps[0]; // Most recent tap
          
          if (newestTap && preferences.taps) {
            const tapperName = newestTap.display_name || newestTap.username || 'Someone';
            
            // Play sound and vibrate
            if (preferences.sound) {
              playNotificationSound();
            }
            if (preferences.vibration) {
              vibrateDevice(newestTap.is_mutual ? [100, 50, 100, 50, 100] : [100, 50, 100]);
            }
            
            // Check if it's a mutual tap
            if (newestTap.is_mutual) {
              toast.success(`🎉 It's a match! You and ${tapperName} tapped each other!`, {
                duration: 5000,
                action: {
                  label: 'View',
                  onClick: () => window.location.href = `/profile/${newestTap.id}`
                }
              });
            } else {
              toast(`⚡ ${tapperName} tapped you!`, {
                duration: 4000,
                action: {
                  label: 'View',
                  onClick: () => window.location.href = `/profile/${newestTap.id}`
                }
              });
            }
            
            // Add to notifications list
            addNotification({
              type: newestTap.is_mutual ? 'match' : 'tap',
              user: newestTap,
              message: newestTap.is_mutual 
                ? `You matched with ${tapperName}!` 
                : `${tapperName} tapped you`
            });
          }
        }
        
        lastTapCountRef.current = currentCount;
      } catch (error) {
        // Silently fail
      }
    };

    // Initial check with slight delay
    const initialTimeout = setTimeout(() => {
      checkForNewTaps();
    }, 2000);

    // Poll every 30 seconds (only as fallback)
    pollingRef.current = setInterval(checkForNewTaps, 30000);

    return () => {
      clearTimeout(initialTimeout);
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [token, user?.id, preferences.taps, preferences.sound, preferences.vibration, addNotification]);

  // Request notification permission
  const requestPermission = useCallback(async () => {
    if (!initialized) {
      toast.info('Notifications are being set up. Please try again in a moment.');
      return false;
    }

    try {
      await OneSignal.Notifications.requestPermission();
      return true;
    } catch (error) {
      console.error('Permission request error:', error);
      toast.error('Unable to enable notifications. Please check your browser settings.');
      return false;
    }
  }, [initialized]);

  // Update preferences
  const updatePreferences = useCallback(async (newPrefs) => {
    try {
      const response = await axios.put(`${API}/users/notification-preferences`, newPrefs);
      setPreferences(response.data.preferences);
      toast.success('Notification preferences updated');
      return true;
    } catch (error) {
      toast.error('Failed to update notification preferences');
      return false;
    }
  }, []);

  // Show prompt to enable notifications
  const showPrompt = useCallback(() => {
    if (!initialized) return;
    
    OneSignal.Slidedown.promptPush();
  }, [initialized]);

  // Mark notification as read
  const markNotificationRead = useCallback((notificationId) => {
    setNotifications(prev => 
      prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
    );
  }, []);

  // Clear all notifications
  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  return (
    <NotificationContext.Provider value={{
      initialized,
      permissionGranted,
      preferences,
      notifications,
      unreadNotificationCount: notifications.filter(n => !n.read).length,
      requestPermission,
      updatePreferences,
      showPrompt,
      markNotificationRead,
      clearNotifications,
      isConfigured: ONESIGNAL_APP_ID && !ONESIGNAL_APP_ID.includes('placeholder')
    }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export default NotificationContext;
